import {useMediaQuery} from "@mantine/hooks";
import {useState, type Dispatch, type KeyboardEvent, type MouseEvent, type Ref, type SetStateAction} from "react";
import ArchiveIcon from "@atlaskit/icon/core/archive-box";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import EditIcon from "@atlaskit/icon/core/edit";
import EyeOpenIcon from "@atlaskit/icon/core/eye-open";
import CommentIcon from "@atlaskit/icon/core/comment";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import {token} from "@atlaskit/tokens";
import type {NewCoreIconProps} from "@atlaskit/icon/base-new";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {TableRowActionsMenuDrawer} from "~/components/table/TableRowActionsMenuDrawer";
import {
    getTableRowActionsMenuHandler,
    getTableRowActionsMenuItemIcon,
    TABLE_ROW_ACTIONS_MENU_ITEMS,
} from "~/components/table/tableRowActionsMenuItems";
import type {
    TableRowActionsConfig,
    TableRowActionsIconComponent,
    TableRowActionsMenuItem,
    TableRowActionsToolbarItem,
} from "~/components/table/tableRowActions.types";

const MOBILE_BREAKPOINT = "(max-width: 47.9375em)";

const DEFAULT_TOOLBAR_ICONS: Record<
    Exclude<TableRowActionsToolbarItem, "more">,
    TableRowActionsIconComponent
> = {
    profile: LinkExternalIcon,
    disposition: RoadmapIcon,
    edit: EditIcon,
    view: EyeOpenIcon,
    logCall: OnCallIcon,
    notes: CommentIcon,
    archive: ArchiveIcon,
};

const DEFAULT_TOOLBAR_LABELS: Record<Exclude<TableRowActionsToolbarItem, "more">, string> = {
    profile: "Open profile",
    disposition: "Disposition",
    edit: "Edit",
    view: "View",
    logCall: "Log a call",
    notes: "Notes",
    archive: "Archive",
};

function stopRowClick(event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();
    const native = event.nativeEvent as Event & {preventOnRowClicked?: boolean};
    native.preventOnRowClicked = true;
}

function withToolbarIconColor(
    Icon: TableRowActionsIconComponent,
    isDisabled: boolean,
    useBrandColorWhenEnabled: boolean,
): TableRowActionsIconComponent {
    function ToolbarIcon(props: NewCoreIconProps) {
        const color =
            isDisabled === true
                ? token("color.icon.disabled")
                : useBrandColorWhenEnabled === true
                  ? token("color.icon.brand")
                  : (props.color ?? token("color.icon"));
        return <Icon {...props} color={color} label={props.label ?? ""} />;
    }
    return ToolbarIcon;
}

function TableToolbarIconButton({
    icon,
    label,
    isSelected,
    isDisabled,
    useBrandColorWhenEnabled = false,
    onClick,
}: {
    icon: TableRowActionsIconComponent
    label: string
    isSelected?: boolean
    isDisabled?: boolean
    useBrandColorWhenEnabled?: boolean
    onClick: (event: MouseEvent<HTMLButtonElement>) => void
}) {
    const resolvedIcon = withToolbarIconColor(icon, isDisabled === true, useBrandColorWhenEnabled);

    return (
        <AnankeIconButton
            type="button"
            spacing="compact"
            appearance="subtle"
            icon={resolvedIcon}
            label={label}
            isSelected={isSelected === true}
            isDisabled={isDisabled === true}
            onClick={onClick}
        />
    );
}

function TableMoreMenu<TRow>({
    row,
    config,
    activeState,
    moreMenuItems,
    isMobile,
    isMoreMenuOpen,
    setIsMoreMenuOpen,
    isMoreTriggerDisabled,
    useBrandToolbarIconColors,
    stopRowClick,
}: {
    row: TRow
    config: TableRowActionsConfig<TRow>
    activeState: ReturnType<NonNullable<TableRowActionsConfig<TRow>["getActiveState"]>>
    moreMenuItems: TableRowActionsMenuItem[]
    isMobile: boolean
    isMoreMenuOpen: boolean
    setIsMoreMenuOpen: Dispatch<SetStateAction<boolean>>
    isMoreTriggerDisabled: boolean
    useBrandToolbarIconColors: boolean
    stopRowClick: (event: MouseEvent | KeyboardEvent) => void
}) {
    const runMenuAction = (item: TableRowActionsMenuItem, handler: ((row: TRow) => void) | undefined) => {
        if (handler === undefined || config.isMenuItemDisabled?.(item, row) === true) {
            return;
        }
        handler(row);
        setIsMoreMenuOpen(false);
    };

    const moreTriggerIcon = withToolbarIconColor(
        ShowMoreHorizontalIcon,
        isMoreTriggerDisabled === true,
        useBrandToolbarIconColors === true,
    );

    const moreTrigger = (
        <AnankeIconButton
            type="button"
            spacing="compact"
            icon={moreTriggerIcon}
            label="More actions"
            appearance="subtle"
            isSelected={activeState.more === true || isMoreMenuOpen}
            isDisabled={isMoreTriggerDisabled === true}
            onClick={(event) => {
                stopRowClick(event);
                if (isMoreTriggerDisabled === true) {
                    return;
                }
                if (isMobile === true) {
                    setIsMoreMenuOpen(true);
                }
            }}
        />
    );

    if (isMobile === true) {
        return (
            <>
                {moreTrigger}
                <TableRowActionsMenuDrawer
                    open={isMoreMenuOpen}
                    setOpen={setIsMoreMenuOpen}
                    row={row}
                    config={config}
                    menuItemIds={moreMenuItems}
                />
            </>
        );
    }

    return (
        <AnankeDropdownMenu
            placement="bottom-end"
            isOpen={isMoreMenuOpen}
            onOpenChange={(args) => {
                setIsMoreMenuOpen(args.isOpen);
            }}
            shouldRenderToParent={false}
            strategy="fixed"
            trigger={({triggerRef, ...triggerProps}) => (
                <AnankeIconButton
                    type="button"
                    spacing="compact"
                    ref={triggerRef as Ref<HTMLButtonElement>}
                    icon={moreTriggerIcon}
                    label="More actions"
                    appearance="subtle"
                    isSelected={activeState.more === true || isMoreMenuOpen}
                    isDisabled={isMoreTriggerDisabled === true}
                    {...triggerProps}
                />
            )}
        >
            <AnankeDropdownItemGroup>
                {moreMenuItems.map((itemId) => {
                    const item = TABLE_ROW_ACTIONS_MENU_ITEMS[itemId];
                    const label = config.menuItemLabels?.[itemId] ?? item.label;
                    const isActive = activeState[itemId] === true;
                    const isDisabled = config.isMenuItemDisabled?.(itemId, row) === true;
                    const handler = getTableRowActionsMenuHandler(itemId, config);
                    const icon =
                        config.getMenuItemIcon?.(itemId, isDisabled) ??
                        getTableRowActionsMenuItemIcon(itemId, isDisabled);

                    return (
                        <AnankeDropdownItem
                            key={itemId}
                            elemBefore={icon}
                            isSelected={isActive}
                            isDisabled={isDisabled}
                            onClick={(event) => {
                                stopRowClick(event);
                                runMenuAction(itemId, handler);
                            }}
                        >
                            {label}
                        </AnankeDropdownItem>
                    );
                })}
                {config.renderMoreMenuItems?.(row)}
            </AnankeDropdownItemGroup>
        </AnankeDropdownMenu>
    );
}

export function TableActionsColumn<TRow>({row, config}: {row: TRow; config: TableRowActionsConfig<TRow>}) {
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT, false, {getInitialValueInEffect: true});
    const activeState = config.getActiveState?.(row) ?? {};
    const canEdit = config.canEdit?.(row) ?? true;
    const useBrandToolbarIconColors = config.useBrandToolbarIconColors === true;

    const isToolbarVisible = (item: TableRowActionsToolbarItem) =>
        config.isToolbarItemVisible?.(item, row) ?? true;

    const isMenuVisible = (item: TableRowActionsMenuItem) => config.isMenuItemVisible?.(item, row) ?? true;

    const isToolbarDisabled = (item: TableRowActionsToolbarItem) =>
        config.isToolbarItemDisabled?.(item, row) === true;

    const moreMenuItems = (config.moreMenu ?? []).filter(isMenuVisible);
    const hasCustomMoreMenuItems = config.renderMoreMenuItems !== undefined;
    const isMoreTriggerDisabled =
        (moreMenuItems.length === 0 && hasCustomMoreMenuItems !== true)
        || (
            moreMenuItems.length > 0
            && moreMenuItems.every((item) => config.isMenuItemDisabled?.(item, row) === true)
            && hasCustomMoreMenuItems !== true
        );

    const renderToolbarItem = (item: TableRowActionsToolbarItem) => {
        if (isToolbarVisible(item) !== true) {
            return null;
        }

        if (item === "more") {
            if (moreMenuItems.length === 0 && hasCustomMoreMenuItems !== true) {
                return null;
            }
            return (
                <TableMoreMenu
                    key={item}
                    row={row}
                    config={config}
                    activeState={activeState}
                    moreMenuItems={moreMenuItems}
                    isMobile={isMobile}
                    isMoreMenuOpen={isMoreMenuOpen}
                    setIsMoreMenuOpen={setIsMoreMenuOpen}
                    isMoreTriggerDisabled={isMoreTriggerDisabled}
                    useBrandToolbarIconColors={useBrandToolbarIconColors}
                    stopRowClick={stopRowClick}
                />
            );
        }

        const icon = config.getToolbarItemIcon?.(item) ?? DEFAULT_TOOLBAR_ICONS[item];
        const label = config.toolbarItemLabels?.[item] ?? DEFAULT_TOOLBAR_LABELS[item];

        if (item === "profile") {
            const isDisabled = isToolbarDisabled("profile");
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.profile === true}
                    isDisabled={isDisabled}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isDisabled === true) {
                            return;
                        }
                        config.onProfile?.(row);
                    }}
                />
            );
        }

        if (item === "disposition") {
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.disposition === true}
                    isDisabled={isToolbarDisabled("disposition")}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isToolbarDisabled("disposition") === true) {
                            return;
                        }
                        config.onDisposition?.(row);
                    }}
                />
            );
        }

        if (item === "edit") {
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.edit === true}
                    isDisabled={canEdit !== true}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (canEdit !== true) {
                            return;
                        }
                        config.onEdit?.(row);
                    }}
                />
            );
        }

        if (item === "view") {
            const isDisabled = isToolbarDisabled("view");
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.view === true}
                    isDisabled={isDisabled}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isDisabled === true) {
                            return;
                        }
                        config.onView?.(row);
                    }}
                />
            );
        }

        if (item === "logCall") {
            const isDisabled = isToolbarDisabled("logCall");
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.logCall === true}
                    isDisabled={isDisabled}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isDisabled === true) {
                            return;
                        }
                        config.onLogCall?.(row);
                    }}
                />
            );
        }

        if (item === "notes") {
            const isDisabled = isToolbarDisabled("notes");
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.notes === true}
                    isDisabled={isDisabled}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isDisabled === true) {
                            return;
                        }
                        config.onNotes?.(row);
                    }}
                />
            );
        }

        if (item === "archive") {
            const isDisabled = isToolbarDisabled("archive");
            return (
                <TableToolbarIconButton
                    key={item}
                    icon={icon}
                    label={label}
                    isSelected={activeState.archive === true}
                    isDisabled={isDisabled}
                    useBrandColorWhenEnabled={useBrandToolbarIconColors}
                    onClick={(event) => {
                        stopRowClick(event);
                        if (isDisabled === true) {
                            return;
                        }
                        config.onArchive?.(row);
                    }}
                />
            );
        }

        return null;
    };

    return (
        <div className="table-actions-cell table-actions-cell--ananke">
            {config.toolbar.map((item) => renderToolbarItem(item))}
        </div>
    );
}
